import React from "react";
import { FormattedMessage } from "react-intl";
import cn from "classnames";
import { useDispatch } from "react-redux";

import { IHandsignData, ITaskEditorProps } from "../../../../../../common/types";

import { setItem, hasItem } from "../../../../../cookie";
import { ICheckboxEvent } from "../../../../../types";
import { editTask } from "../../../../../store/plan/actions";
import { post } from "../../../../../actions/general";

import ScoreEditor from "../../../../Score";
import { Checkbox } from "../../../../Form";

import EditorBar, {
    FlexLeft,
    FlexRight,
    Center,
    Changer,
} from "../../../EditorBar";

import getScale from "../../Common/getScale";
import getScore from "../../Common/getScore";
import getTempo from "../../Common/getTempo";
import AudioUploader from "../../Common/AudioUploader";
import ChangeColor from "../../Common/ChangeColor";
import ChangeScale from "../../Common/ChangeScale";
import ChangeTempo from "../../Common/ChangeTempo";
import useExerciseEditor from "../../Common/useExerciseEditor";

import messages from "./messages";

const HandSignEditor = ({ taskIndex, plan, toggleInlineEditor, publicView, inlineEditorOpen, reload }: ITaskEditorProps) => {
    const {
        openChanger,
        playable,
        handleChangeOpen,
        setPopupRef,
        storeScore,
        changeTempo,
        setPlayable,
        exportScore,
    } = useExerciseEditor({ plan, inlineEditorOpen, taskIndex });
    const dispatch = useDispatch();
    const changeHint = (e: ICheckboxEvent) => {
        let hint = e.target.checked;
        dispatch(editTask({ taskIndex, changingProp: "hint", val: hint }));
        dispatch(post("rhyt-name-hint", {
            planId: plan._id,
            index: taskIndex,
            hint,
        }));
    };

    const changeShowPitches = ({ target: { checked } }: ICheckboxEvent) => {
        dispatch(editTask({ taskIndex, changingProp: "showPitches", val: checked }));
        dispatch(post("task-type/change-show-pitches", {
            planId: plan._id,
            index: taskIndex,
            showPitches: checked,
        }));
        if (!inlineEditorOpen) {
            reload!();
        }
    };
    const task = plan.tasks[taskIndex];
    const data = task.data as IHandsignData;
    const forwardProps = {
        plan,
        taskIndex,
        openChanger,
        handleChangeOpen,
        setPopupRef,
    }
    return (
        <div>
            {inlineEditorOpen && (
                <ScoreEditor
                    taskType="handSign"
                    score={JSON.parse(JSON.stringify(getScore(plan, task) || {}))}
                    scale={getScale(plan, task)}
                    tempo={getTempo(plan, task)}
                    setScore={storeScore}
                    changeTempo={changeTempo}
                />
            )}
            {!publicView && (
                <EditorBar>
                    <FlexLeft>
                        {data && <ChangeScale {...forwardProps} />}
                        <Changer>
                            <Checkbox
                                dark
                                label={<FormattedMessage {...messages.showPitches} />}
                                checked={data.showPitches || false}
                                onChange={changeShowPitches}
                            />
                        </Changer>
                        <AudioUploader plan={plan} taskIndex={taskIndex} />
                    </FlexLeft>
                    <Center
                        disabled={(inlineEditorOpen && !playable)}
                        tooltip={!playable && inlineEditorOpen && <FormattedMessage {...messages.createScoreTooltip} />}
                        onClick={() => {
                            toggleInlineEditor(!inlineEditorOpen);
                            setPlayable(true);
                            if (inlineEditorOpen) {
                                exportScore();
                            }
                            setItem("rhytNameDone", "1", 60 * 60 * 24 * 365, "/");
                        }}
                        className={cn(
                            !(inlineEditorOpen && !playable) && !hasItem("rhytNameDone") && !task.ref && "pulsate"
                        )}
                    >
                        {inlineEditorOpen ? (
                            <FormattedMessage {...messages.stopEdit} />
                        ) : (
                            <FormattedMessage {...messages.editScore} />
                        )}
                    </Center>
                    <FlexRight>
                        <AudioUploader plan={plan} taskIndex={taskIndex} isSolution />
                        <Changer>
                            <Checkbox
                                dark
                                label={<FormattedMessage {...messages.firstKnown} />}
                                checked={data.hint === undefined ? true : !!data.hint}
                                onChange={changeHint}
                            ></Checkbox>
                        </Changer>
                        <ChangeTempo {...forwardProps} />
                        <ChangeColor
                            {...forwardProps}
                            color={data?.color}
                        />
                    </FlexRight>
                </EditorBar>
            )}
        </div>
    );
};

export default HandSignEditor;
